// import hooks
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCreateResource } from 'RestAPIs/useCreateResource';

// import api's and mutations
import { CRON_JOBS } from 'RestAPIs/endpoints';

// import types
import { CronJobPayload, CronJobProps } from './CronJobsCRUDForm.types';

// import components
import { LoadingButton } from '@mui/lab';
import { ControlledTextField } from 'components/shared';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import {
  CronJobsCRUDFormActionsBox,
  CronJobsCRUDFormBox
} from './CronJobsCRUDForm.styles';

// import utils
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

const cronSchema = Yup.object().shape({ cron: Yup.string() });

export const CronJobsCRUDForm: React.FC<CronJobProps> = ({
  onClose,
  children,
  schema,
  convertData,
  defaultValues
}) => {
  const { t } = useTranslation();
  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        ...cronSchema.fields,
        ...schema.fields
      })
    ),
    defaultValues: schema.cast(defaultValues ?? {})
  });

  const { mutateAsync: handleCreate, isLoading } =
    useCreateResource<CronJobPayload>({
      url: CRON_JOBS,
      invalidateQueryKey: ['cron-jobs', 'list']
    });

  const submitForm = (data: CronJobPayload) => {
    handleCreate(convertData ? convertData(data) : data).then(onClose);
  };

  return (
    <MultiStepForm
      methods={methods}
      onSubmit={methods.handleSubmit(submitForm)}
    >
      <CronJobsCRUDFormBox>
        {children}
        <ControlledTextField
          name="cron"
          control={methods.control}
          variant="outlined"
          label={t('cronJobs.form.cronSchema')}
        />
        <span>
          {t('cronJobs.form.formatExample')}
          <a
            href={'http://www.cronmaker.com/'}
            target={'_blank'}
            rel="noreferrer"
          >
            {t('cronJobs.form.readMoreHere')}
          </a>
        </span>
      </CronJobsCRUDFormBox>
      <MultiStepForm.Actions>
        <CronJobsCRUDFormActionsBox>
          <LoadingButton
            loading={isLoading}
            type="submit"
            variant="contained"
            autoFocus
            color="primary"
          >
            {t('save')}
          </LoadingButton>
        </CronJobsCRUDFormActionsBox>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};
